import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from 'primereact/button'

/**
 * Renders an unauthorized access page with a 403 error message
 *
 * @component
 * @description
 * This component displays a user-friendly error page when access is denied.
 * Features:
 * - Shows 403 error code
 * - Displays translated error message and description
 * - Provides a button to navigate back to home
 * - Responsive layout for mobile and desktop
 * - Uses i18n for all text content
 *
 * @example
 * ```tsx
 * <Unauthorized />
 * ```
 *
 * @returns {JSX.Element} A responsive error page component
 */

export const Unauthorized: React.FC = () => {
  const { t } = useTranslation()

  return (
    <div className="py-6">
      <div>
        <title>
          {t('components.unauthorized.title')} | {t('common.app.title')}
        </title>
      </div>
      <div className="flex flex-column flex-grow-1 align-items-center justify-content-center">
        <div className="flex flex-column sm:flex-row">
          <div className="text-5xl text-primary font-bold border-left-1 sm:border-right-1 sm:border-left-none px-3 pb-3 sm:pb-0">
            {t('components.unauthorized.content.code')}
          </div>
          <div className="flex flex-column border-left-1 sm:border-left-none border-primary px-3 gap-2">
            <div className="font-bold">
              {t('components.unauthorized.content.text')}
            </div>
            <div>{t('components.unauthorized.content.description')}</div>
            <div className="mt-4">
              <Link to="/" className="no-underline">
                <Button>{t('common.buttons.backToHome')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
